import React, { useEffect, useContext, useState } from "react";
import { useHistory, useParams } from "react-router-dom";

//axios and base url
import axios from "axios";
import { BASE_URL } from "../../../../../BaseUrl";

//functions
import {
  _t,
  getCookie,
  tableLoading,
  customStyle,
  formatPrice,
} from "../../../../../functions/Functions";
import { useTranslation } from "react-i18next";
import { useCustomHooks } from "../../../../../functions/Hooks";

//3rd party packages
import { Helmet } from "react-helmet";
import Select from "react-select";
import makeAnimated from "react-select/animated";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

//pages & includes
import ManageSidebar from "../ManageSidebar";

//context consumer
import { RestaurantContext } from "../../../../../contexts/Restaurant";
import { UserContext } from "../../../../../contexts/User";
import { useGeneralHooks } from "../../../../../hooks/generalHooks";
import { ToastError, ToastSuccess } from "../../../../../functions/ToastShow";

const StockReceivingAction = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const { id } = useParams();
  const { roundOffStock, qtyAmountPoints } = useCustomHooks();
  const { issueQtyLock } = useGeneralHooks();

  let {
    loading,
    setLoading,
    branchForSearch,
    branchListWeb,
  } = useContext(RestaurantContext);

  let { authUserInfo } = useContext(UserContext);

  let [formData, setFormData] = useState({
    document_no: "",
    ref_identity:"",
    requisition_doc_identity: "",
    issuance_doc_identity: "",
    fbranch: null,
    tbranch: null,
    remarks: "",
  });

  const [newRow, setNewRow] = useState([]);
  const [documentDate, setDocumentDate] = useState(new Date());
  const [branchSearch, setBranchSearch] = useState([]);
  const [storeReqDoc, setStoreReqDoc] = useState([]);

  useEffect(() => {
    if (
      authUserInfo?.details.user_type === "manager" ||
      authUserInfo?.details.user_type === "staff"
    ) {
      setBranchSearch(
        branchForSearch.filter(
          (branch) => branch.id === authUserInfo.details.branch_id
        )
      );
    } else {
      setBranchSearch(branchForSearch);
    }
  }, []);

  const getPreviousStock = async (
    index,
    itemId,
    unitId,
    branchId = formData.fbranch?.id
  ) => {
    if (!branchId || !itemId || !unitId) {
      return false;
    }

    setNewRow((currentRows) => {
      const updated = [...currentRows];
      if (!updated[index]) {
        return currentRows;
      }
      updated[index] = { ...updated[index], previous_stock: "Loading.." };
      return updated;
    });

    const url = BASE_URL + "/settings/get-qty-avgrate";
    const payload = {
      branch_id: branchId,
      item_id: itemId,
      unit_id: unitId,
    };

    try {
      const res = await axios.post(url, payload, {
        headers: { Authorization: `Bearer ${getCookie()}` },
      });
      setNewRow((currentRows) => {
        const updated = [...currentRows];
        if (!updated[index]) {
          return currentRows;
        }
        updated[index] = {
          ...updated[index],
          previous_stock: roundOffStock(Number(res.data.stock_qty) || 0),
        };
        return updated;
      });
      return true;
    } catch (error) {
      setNewRow((currentRows) => {
        const updated = [...currentRows];
        if (updated[index]) {
          updated[index] = { ...updated[index], previous_stock: "" };
        }
        return updated;
      });
      ToastError({ title: _t(t("Please try again later")) });
      return false;
    }
  };

  const fetchRowsPreviousStock = (rows, branchId) => {
    if (!branchId || !rows?.length) {
      return;
    }
    rows.forEach((row, index) => {
      const itemId = row.item_id || row.item?.id;
      const unitId = row.unit_id || row.unit?.id;
      if (itemId && unitId) {
        getPreviousStock(index, itemId, unitId, branchId);
      }
    });
  };

  const getFinalStockCount = (item) => {
    if (
      item.previous_stock === "Loading.." ||
      item.previous_stock === "" ||
      item.previous_stock == null
    ) {
      return item.previous_stock === "Loading.." ? "Loading.." : "-";
    }
    return roundOffStock(
      (Number(item.previous_stock) || 0) + (Number(item.received_qty) || 0)
    );
  };

  const populateEditForm = (data) => {
    setFormData({
      ...formData,
      is_post: data.is_post,
      document_no: data.document_identity,
      fbranch: data.from_branch,
      tbranch: data.to_branch,
      ref_identity:{
        id: null,
        document_identity: data.issuance_doc_identity
      },
      requisition_doc_identity: data?.requisition_doc_identity,
      issuance_doc_identity: data?.issuance_doc_identity,
      remarks: data.remarks,
    });

    setDocumentDate(new Date(data.document_date));

    if (data.stock_acceptance_detail?.length > 0) {
      const rowArr = data.stock_acceptance_detail.map((item) => ({
        item: { id: item.item_id, name: item.item_name },
        unit: { id: item.unit_id, name: item.unit_name },
        item_id: item.item_id,
        item_name: item.item_name,
        unit_name: item.unit_name,
        unit_id: item.unit_id,
        qty: item.qty,
        remarks: item.item_remarks,
        received_qty: item.qty,
        requisition_qty: item.requisition_qty,
        issued_qty: item.issued_qty,
        base_unit: item.base_unit || "",
        base_qty: item.base_qty || "",
        conversion_qty: item.conversion_qty || "",
        previous_stock: "",
      }));
      setNewRow(rowArr);
      fetchRowsPreviousStock(rowArr, data.from_branch?.id);
    }
  };

  const fetchReceivingItem = async () => {
    setLoading(true);
    const url = BASE_URL + "/settings/stock-recieving/" + parseInt(id);
    try {
      const res = await axios.get(url, {
        headers: { Authorization: `Bearer ${getCookie()}` },
      });
      if (res) {
        populateEditForm(res.data);
      }
      setLoading(false);
    } catch {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (id) {
      fetchReceivingItem();
    }
  }, [id]);

  const getStoreReqDoc = async (toBranchId) => {
    const url = BASE_URL + "/settings/get-stock-issuance-data/";
    await axios
      .get(
        url + formData.fbranch.id + "/" + toBranchId,
        { headers: { Authorization: `Bearer ${getCookie()}` } }
      )
      .then((res) => {
        if (res.data) {
          setStoreReqDoc(res.data || []);
        }
      })
      .catch((error) => {
        console.log("check error", error);
      });
  };

  const getStoreReqItems = async (ref_doc) => {
    const url = BASE_URL + "/settings/get_stock_issuance_detail/" + ref_doc.id;
    await axios
      .get(url, { headers: { Authorization: `Bearer ${getCookie()}` } } )
      .then((res) => {
        if (res?.data?.stock_issuance_detail) {

          setFormData({ ...formData, 
            ref_identity: ref_doc,
            requisition_doc_identity: res.data.ref_identity, 
            issuance_doc_identity: res.data.document_identity 
          });

          const rowArr = res.data.stock_issuance_detail.map((item) => ({
            ...item,
            max_qty: Number(item.qty),
            remarks: item?.item_remarks ?? "",
            issued_qty: item.qty,
            received_qty: item.qty,
            requisition_qty: item.requisition_qty,
            base_unit: item.base_unit || "",
            base_qty: item.base_qty || "",
            conversion_qty: item.conversion_qty || "",
            previous_stock: "",
          }));

          setNewRow(rowArr);
          fetchRowsPreviousStock(rowArr, formData.fbranch?.id);
        }
      })
      .catch((error) => {
        console.log("check error", error);
      });
  };

  const handleItemQty = (index, e) => {
    newRow[index].received_qty = e.target.value;
    setNewRow([...newRow]);
  };

  const calBaseQty=(index)=>{
    let qty=newRow[index].received_qty;
    let conversion_qty=newRow[index].conversion_qty
    if(qty && conversion_qty){
      return roundOffStock(qty * conversion_qty);
    }else{
      return "";
    }
  }

  const handleSetFromBranch = (fbranch) => {
    setFormData({ ...formData, fbranch });
  };

  const handleSetToBranch = (tbranch) => {
    setFormData({ ...formData, tbranch, ref_identity: "" });
    getStoreReqDoc(tbranch?.id);
    setNewRow([]);
  };

  const handleSetDocId = (ref_identity) => {
    setFormData({ ...formData, ref_identity });
    getStoreReqItems(ref_identity);
  };

  const handleRemarks = (e) => {
    setFormData({ ...formData, remarks: e.target.value });
  };

  const handleSave = async () => {
    const d = new Date(documentDate);
    const date = `${d.getFullYear()}-${d.getMonth() + 1}-${d.getDate()}`;

    let isQtyOverflow = false;
    let itemDetails = [];

    newRow.map((row, index) => {
      if(parseFloat(row.received_qty) > parseFloat(row.issued_qty)){
        isQtyOverflow = true;
      }
      itemDetails.push({
        qty: parseFloat(row.received_qty),
        issued_qty: parseFloat(row.issued_qty),
        requisition_qty: parseFloat(row.requisition_qty),
        item_id: row.item_id,
        cost_price: row.cost_price ?? 0,
        sell_price: row?.sell_price ?? 0,
        amount: formatPrice(parseFloat(row?.sell_price ?? 0) * parseFloat(row.received_qty)),
        item_name: row.item_name,
        unit_id: row.unit_id,
        unit_name: row.unit_name,
        item_remarks: row.remarks,
        base_unit: row.base_unit,
        base_qty: calBaseQty(index),
        conversion_qty: parseFloat(row.conversion_qty),
      });
    });

    if (isQtyOverflow) {
      ToastError({
        title: _t(t("Your qty should not be greater than the issued qty.")),
      });

      return;
    } 

    if (newRow.length === itemDetails.length) {
      setLoading(true);
      const payload = {
        ...(id && { id }),
        from_branch_id: formData.fbranch.id,
        to_branch_id: formData.tbranch.id,
        requisition_doc_identity: formData.requisition_doc_identity,
        issuance_doc_identity: formData.issuance_doc_identity,
        document_date: date,
        remarks: formData.remarks,
        items: itemDetails,
      };
      const url = BASE_URL + "/settings/stock-recieving"+ (id ? `/${id}` : "");
      try {
        await axios[id ? "put" : "post"](url, payload, {
          headers: { Authorization: `Bearer ${getCookie()}` },
        });
        setLoading(false);
        ToastSuccess({ title: _t(t("Stock receiving has been saved")) });
        history.push("/dashboard/manage/stock/stock-receiving");
      } catch (error) {
        console.log("check error==>", error);
        setLoading(false);
        ToastError({ title: _t(t("Please try again later")) });
      }
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (
      formData.fbranch !== null &&
      formData.tbranch !== null &&
      formData.document_no !== null &&
      documentDate !== null && formData?.requisition_doc_identity !== null && formData?.issuance_doc_identity !== null
    ) {
      if (newRow.length > 0) {
        handleSave();
      } else {
        ToastError({ title: _t(t("Please select doc with item")) });
      }
    } else {
      ToastError({ title: _t(t("Please fill all the required fields")) });
    }
  };

  const pageTitle = id
    ? formData?.is_post
      ? _t(t("View Stock Receiving"))
      : _t(t("Edit Stock Receiving"))
    : _t(t("Add Stock Receiving"));

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
      </Helmet>

      <main id="main" data-simplebar>
        <div className="container-fluid">
          <div className="row t-mt-10 gx-2">
            {/* left Sidebar */}
            <div className="col-lg-2 col-xxl-2 t-mb-30 mb-lg-0">
              <ManageSidebar />
            </div>

            {/* Rightbar contents */}
            <div className="col-lg-10 col-xxl-10 t-mb-30 mb-lg-0">
              <div className="t-bg-white">
                <div
                  className="fk-scroll--pos-menu table-bottom-info-hide"
                  data-simplebar
                >
                  <div className="t-pl-15 t-pr-15">
                    {loading === true ? (
                      tableLoading()
                    ) : (
                      <div key="smtp-form">
                        <div className="row gx-2 align-items-center t-pt-15 t-pb-15">
                          <div className="col-md-6 col-lg-5 t-mb-15 mb-md-0">
                            <ul className="t-list fk-breadcrumb">
                              <li className="fk-breadcrumb__list">
                                <span className="t-link fk-breadcrumb__link text-capitalize">
                                  {pageTitle}
                                </span>
                              </li>
                            </ul>
                          </div>
                          <div className="col-md-6 col-lg-7">
                            <div className="row gx-3 align-items-center"></div>
                          </div>
                        </div>

                        <form
                          className="row card p-2 mx-3 mb-5 table-text"
                          onSubmit={handleSubmit}
                          autoComplete="off"
                        >
                          <div className="col-12">
                            <div className="row">
                              <div className="form-group col-3 mt-3">
                                <div className="mb-2">
                                  <label htmlFor="document_no" className="control-label">
                                    {_t(t("Document no"))}
                                    <span className="text-danger">*</span>
                                  </label>
                                </div>
                                <div className="mb-2">
                                  <input
                                    readOnly
                                    type="text"
                                    className="form-control table-text"
                                    id="document_no"
                                    name="document_no"
                                    value={formData.document_no}
                                    placeholder="Auto no"
                                    required
                                  />
                                </div>
                              </div>

                              <div className="form-group col-3 text-right mt-3">
                                <div className="mb-2 text-left">
                                  <label htmlFor="transfer_date" className="control-label">
                                    {_t(t("Document Date"))}
                                    <span className="text-danger">*</span>
                                  </label>
                                </div>
                                <div className="mb-2">
                                  <DatePicker
                                    selected={documentDate}
                                    onChange={(date) => setDocumentDate(date)}
                                    peekNextMonth
                                    showMonthDropdown
                                    showYearDropdown
                                    dropdownMode="select"
                                    className="form-control table-text"
                                    placeholderText={_t(t("Document date"))}
                                    shouldCloseOnSelect={false}
                                  />
                                </div>
                              </div>

                              <div className="form-group mt-3 col-3">
                                <div className="mb-2">
                                  <label htmlFor="branch" className="control-label">
                                    {_t(t("To Branch"))}
                                    <span className="text-danger">*</span>
                                  </label>
                                </div>
                                <Select
                                  options={branchSearch}
                                  components={makeAnimated()}
                                  getOptionLabel={(option) => option.name}
                                  getOptionValue={(option) => option.id}
                                  value={formData.fbranch || null}
                                  classNamePrefix="select"
                                  onChange={handleSetFromBranch}
                                  maxMenuHeight="200px"
                                  styles={customStyle}
                                  placeholder={_t(t("Please select a branch")) + ".."}
                                />
                              </div>
                              <div className="form-group mt-3 col-3">
                                <div className="mb-2">
                                  <label htmlFor="branch" className="control-label">
                                    {_t(t("From Branch"))}
                                    <span className="text-danger">*</span>
                                  </label>
                                </div>
                                <Select
                                  options={
                                    branchListWeb
                                      ? branchListWeb.filter(
                                          (item) => item.id !== formData?.fbranch?.id
                                        )
                                      : []
                                  }
                                  components={makeAnimated()}
                                  getOptionLabel={(option) => option.name}
                                  getOptionValue={(option) => option.id}
                                  value={formData.tbranch || null}
                                  classNamePrefix="select"
                                  onChange={handleSetToBranch}
                                  maxMenuHeight="200px"
                                  isDisabled={!formData.fbranch}
                                  styles={customStyle}
                                  placeholder={_t(t("Please select to branch")) + ".."}
                                />
                              </div>
                            </div>

                            <div className="row">

                              <div className="form-group mt-2 col-3">
                                <div className="mb-2">
                                  <label htmlFor="branch" className="control-label">
                                    {_t(t("Select Document Ref"))}
                                    <span className="text-danger">*</span>
                                  </label>
                                </div>
                                <Select
                                  options={storeReqDoc}
                                  getOptionLabel={(option) => option.document_identity + (option?.ref_identity ? " (" + option?.ref_identity + ")" : "")}
                                  getOptionValue={(option) => option.id}
                                  value={formData.ref_identity || null}
                                  classNamePrefix="select"
                                  onChange={handleSetDocId}
                                  isDisabled={!formData.fbranch || formData.is_post}
                                  maxMenuHeight="200px"
                                  styles={customStyle}
                                  placeholder={_t(t("Please select doc")) + ".."}
                                />
                              </div>

                              <div className="form-group mt-2 col-3">
                                <div className="mb-2">
                                  <label htmlFor="branch" className="control-label">
                                    {_t(t("Request Ref"))}
                                    <span className="text-danger">*</span>
                                  </label>
                                </div>
                                <input 
                                  readOnly
                                  type="text"
                                  className="form-control table-text"
                                  value={formData.issuance_doc_identity}
                                />
                              </div>

                              <div className="form-group mt-2 col-6">
                                <div className="mb-2">
                                  <label htmlFor="remarks" className="control-label">
                                    {_t(t("Remarks"))}
                                  </label>
                                </div>
                                <div className="mb-2">
                                  <textarea
                                    className="form-control table-text"
                                    id="remarks"
                                    name="remarks"
                                    onChange={handleRemarks}
                                    value={formData.remarks || ""}
                                    placeholder="Remarks"
                                    style={{ height: "33px" }}
                                  />
                                </div>
                              </div>
                            </div>

                            <div
                              className="table-responsive mt-3 pr-1"
                              style={{ maxHeight: "23rem", overflow: "auto" }}
                            >
                              <table className="table sticky-table-header table-bordered-sm table-hover">
                                <thead className="align-middle">
                                  <tr>
                                    <th
                                      style={{minWidth:"150px"}}
                                      scope="col"
                                      className="table-text text-capitalize align-middle text-center border-1 border table_child"
                                    >
                                      <span style={{ fontWeight: 500 }}>
                                        {_t(t("Item Name"))}
                                      </span>
                                    </th>
                                    <th
                                      scope="col"
                                      className="table-text text-capitalize align-middle text-center border-1 border"
                                    >
                                      <span style={{ fontWeight: 500 }}>
                                        {_t(t("Requisition Qty"))}
                                      </span>
                                    </th>
                                    <th
                                      scope="col"
                                      className="table-text text-capitalize align-middle text-center border-1 border"
                                    >
                                      <span style={{ fontWeight: 500 }}>
                                        {_t(t("Previous Stock"))}
                                      </span>
                                    </th>
                                    <th
                                      scope="col"
                                      className="table-text text-capitalize align-middle text-center border-1 border"
                                    >
                                      <span style={{ fontWeight: 500 }}>
                                        {_t(t("Issued Qty"))}
                                      </span>
                                    </th>
                                    <th
                                      scope="col"
                                      className="table-text text-capitalize align-middle text-center border-1 border"
                                    >
                                      <span style={{ fontWeight: 500 }}>
                                        {_t(t("Received Qty"))}
                                      </span>
                                    </th>
                                    <th
                                      scope="col"
                                      className="table-text text-capitalize align-middle text-center border-1 border"
                                    >
                                      <span style={{ fontWeight: 500 }}>
                                        {_t(t("Final Stock Count"))}
                                      </span>
                                    </th>
                                    <th
                                      scope="col"
                                      className="table-text text-capitalize align-middle text-center border-1 border"
                                    >
                                      <span style={{ fontWeight: 500 }}>
                                        {_t(t("Unit"))}
                                      </span>
                                    </th>
                                    <th
                                      scope="col"
                                      className="table-text text-capitalize align-middle text-center border-1 border"
                                    >
                                      <span style={{ fontWeight: 500 }}>
                                        {_t(t("Remarks"))}
                                      </span>
                                    </th>
                                  </tr>
                                </thead>
                                {newRow.length > 0 && (
                                  <tbody className="align-middle">
                                    {newRow.map((item, index) => (
                                      <tr className="align-middle" key={index}>
                                        <td className="table-text text-capitalize align-middle text-center">
                                          {item?.item_name}
                                        </td>
                                        <td className="table-text text-capitalize align-middle text-center">
                                          {item.requisition_qty}
                                        </td>
                                        <td
                                          className="table-text text-capitalize align-middle text-center"
                                          style={{ backgroundColor: "#f1f5f9" }}
                                        >
                                          {item.previous_stock === "Loading.."
                                            ? "Loading.."
                                            : item.previous_stock ||
                                              item.previous_stock === 0
                                              ? item.previous_stock
                                              : "-"}
                                        </td>
                                        <td className="table-text text-capitalize align-middle text-center">
                                          {item.issued_qty}
                                        </td>
                                        <td className="table-text text-capitalize align-middle text-center">
                                          <input
                                            disabled={formData.is_post || issueQtyLock}
                                            type="number"
                                            step={qtyAmountPoints()}
                                            min="0"
                                            onKeyDown={(evt) =>
                                              ["e", "E", "+", "-", "ArrowUp", "ArrowDown"].includes(
                                                evt.key
                                              ) && evt.preventDefault()
                                            }
                                            onWheel={(e) => e.target.blur()}
                                            className="form-control text-center table-Input table-text"
                                            onChange={(e) => handleItemQty(index, e)}
                                            value={item?.received_qty || ""}
                                            placeholder="Received Qty"
                                            required
                                          />
                                        </td>
                                        <td
                                          className="table-text text-capitalize align-middle text-center"
                                          style={{ backgroundColor: "#f1f5f9" }}
                                        >
                                          {getFinalStockCount(item)}
                                        </td>
                                        <td className="table-text text-capitalize align-middle text-center">
                                          {item?.unit_name}
                                        </td>
                                        {/* <td className="table-text align-middle text-center">
                                          {item.base_unit}
                                        </td> 
                                        <td className="table-text align-middle text-center">                                                
                                          {formatPrice(calBaseQty(index))}
                                        </td>
                                        <td className="table-text align-middle text-center">
                                          {item.conversion_qty}
                                        </td> */}
                                        <td className="table-text text-capitalize align-middle text-center">
                                          {item?.remarks || "-"}
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                )}
                              </table>
                            </div>

                            <div className="d-flex mt-2 justify-content-end align-items-center">
                              <div className="col-3 d-flex justify-content-end">
                                <button
                                  type="button"
                                  className="btn rounded hov-btn mr-3"
                                  onClick={() =>
                                    history.push("/dashboard/manage/stock/stock-receiving")
                                  }
                                >
                                  <i className="fa fa-arrow-left" aria-hidden="true"></i>{" "}
                                  {_t(t("Back"))}
                                </button>
                                {!formData?.is_post && (
                                  <button
                                    disabled={loading}
                                    type="submit"
                                    className="btn btn-primary rounded"
                                  >
                                    <i className="fa fa-floppy-o" aria-hidden="true"></i>{" "}
                                    {_t(t("Save"))}
                                  </button>
                                )}
                              </div>
                            </div>
                          </div>
                        </form>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </>
  );
};

export default StockReceivingAction;
